import { makeTheme } from 'dripsy';
import { Platform } from 'react-native';
const COLOR_BLACK = '#050400';
const COLOR_WHITE = '#FFF';
const COLOR_GRAY = '#EEEBE6';
const COLOR_GRAY_LIGHT = '#F4F3EF';
const COLOR_GRAY_LIGHTEST = '#F9F9F7';
const COLOR_BROWN_900 = '#2F2A23';
const COLOR_BROWN_800 = '#3C3931';
const COLOR_BROWN_600 = '#615E54';
const COLOR_BROWN_400 = '#C0BAAE';
const COLOR_BROWN_200 = '#E6E2DA';
const COLOR_YELLOW_DARK = '#E0A100';
const COLOR_YELLOW = '#F3BB0B';
const COLOR_YELLOW_LIGHT = '#FBE8A8';
const COLOR_YELLOW_LIGHTEST = '#FFF5D6';
const COLOR_ORANGE_DARK = '#AE4B00';
const COLOR_ORANGE = '#FF760F';
const COLOR_ORANGE_LIGHT = '#FFCFAB';
const COLOR_ORANGE_LIGHTEST = '#FFECDE';
const COLOR_GREEN_DARK = '#617103';
const COLOR_GREEN = '#ACC806';
const COLOR_GREEN_LIGHT = '#E2ECA8';
const COLOR_GREEN_LIGHTEST = '#F4F7DF';
const COLOR_BLUE_DARK = '#1868C7';
const COLOR_BLUE = '#589FF1';
const COLOR_BLUE_LIGHT = '#B3DBFF';
const COLOR_BLUE_LIGHTEST = '#E5F4FF';
const COLOR_RED_DARK = '#8B1407';
const COLOR_RED = '#F4230B';
const COLOR_RED_LIGHT = '#FFD6D1';
const COLOR_RED_LIGHTEST = '#FFF3F2';
const FONT_SIZES = {
    $0: 12,
    $1: 14,
    $2: 16,
    $3: 20,
    $4: 24,
    $5: 32,
    $6: 40,
    $7: 48,
    $8: 56,
};
const SPACE_AND_SIZES = {
    $0: 0,
    $1: 4,
    $2: 8,
    $3: 12,
    $4: 16,
    $5: 20,
    $6: 24,
    $7: 32,
    $8: 40,
    $9: 48,
    $10: 56,
    $11: 64,
    $12: 72,
    $13: 80,
    $14: 96,
    $15: 112,
    $16: 128,
    $17: 144,
};
const fontName = 'GreycliffCF';
const webFont = (font) => Platform.select({
    web: `${font}, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
    default: font,
});
const theme = makeTheme({
    colors: {
        text: COLOR_BLACK,
        background: COLOR_WHITE,
        border: COLOR_GRAY,
        backgroundMedium: COLOR_GRAY,
        backgroundWeak: COLOR_GRAY_LIGHT,
        backgroundWeakest: COLOR_GRAY_LIGHTEST,
        primaryBackgroundHover: COLOR_YELLOW_DARK,
        primaryBackground: COLOR_YELLOW,
        primaryBackgroundActive: COLOR_YELLOW_LIGHT,
        primaryBackgroundWeak: COLOR_YELLOW_LIGHTEST,
        secondaryBackground: COLOR_BROWN_800,
        secondaryIcon: COLOR_BROWN_800,
        secondaryBackgroundHover: COLOR_BROWN_900,
        secondaryText: COLOR_BROWN_600,
        secondaryBackgroundActive: COLOR_BROWN_400,
        secondaryBackgroundDisabled: COLOR_BROWN_200,
        secondaryIconDisabled: COLOR_BROWN_200,
        secondaryBorderDisabled: COLOR_BROWN_200,
        warningBackground: COLOR_ORANGE,
        warningIcon: COLOR_ORANGE,
        warningBackgroundHover: COLOR_ORANGE_DARK,
        warningBackgroundActive: COLOR_ORANGE_LIGHT,
        warningBackgroundWeak: COLOR_ORANGE_LIGHTEST,
        successBackground: COLOR_GREEN,
        successIcon: COLOR_GREEN,
        successBackgroundHover: COLOR_GREEN_DARK,
        successBackgroundActive: COLOR_GREEN_LIGHT,
        successBackgroundWeak: COLOR_GREEN_LIGHTEST,
        infoBackground: COLOR_BLUE,
        infoIcon: COLOR_BLUE,
        infoBackgroundHover: COLOR_BLUE_DARK,
        infoBackgroundActive: COLOR_BLUE_LIGHT,
        infoBackgroundWeak: COLOR_BLUE_LIGHTEST,
        infoBorder: COLOR_BLUE,
        errorText: COLOR_RED,
        errorBackground: COLOR_RED,
        errorBorder: COLOR_RED,
        errorIcon: COLOR_RED,
        errorBackgroundHover: COLOR_RED_DARK,
        errorBackgroundActive: COLOR_RED_LIGHT,
        errorBackgroundWeak: COLOR_RED_LIGHTEST,
    },
    space: { ...SPACE_AND_SIZES },
    sizes: { ...SPACE_AND_SIZES },
    fontSizes: { ...FONT_SIZES },
    lineHeights: { ...FONT_SIZES },
    radii: {
        none: 0,
        subtle: 4,
        default: 8,
        full: 9999,
    },
    customFonts: {
        [fontName]: {
            bold: webFont('GreycliffCF-Bold'),
            default: webFont(fontName),
            normal: webFont(fontName),
            100: webFont('GreycliffCF-Thin'),
            200: webFont('GreycliffCF-ExtraLight'),
            300: webFont('GreycliffCF-Light'),
            400: webFont(fontName),
            500: webFont('GreycliffCF-Medium'),
            600: webFont('GreycliffCF-DemiBold'),
            700: webFont('GreycliffCF-Bold'),
            800: webFont('GreycliffCF-ExtraBold'),
            900: webFont('GreycliffCF-Heavy'),
        },
    },
    fonts: {
        root: fontName,
        monospace: 'Menlo, Monaco, "Lucida Console", "Liberation Mono", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Courier New", monospace',
    },
    fontWeights: {
        bold: '700',
    },
    zIndices: {
        $0: 0,
        $1: 1,
    },
    text: {
        h1: {
            fontWeight: 'bold',
        },
    },
    buttons: {
        primary: {
            fontWeight: 'bold',
            borderRadius: 'default',
            backgroundColor: 'primaryBackground',
            color: 'text',
            outlineColor: 'primaryBackground',
            borderColor: 'transparent',
            $$hover: {
                backgroundColor: 'primaryBackgroundHover',
            },
            $$active: {
                backgroundColor: 'primaryBackgroundActive',
            },
            $$disabled: {
                color: 'secondaryText',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
        },
        secondary: {
            fontWeight: 'bold',
            borderRadius: 'default',
            backgroundColor: 'secondaryBackground',
            color: 'background',
            outlineColor: 'secondaryBackground',
            borderColor: 'transparent',
            $$hover: {
                backgroundColor: 'secondaryBackgroundHover',
            },
            $$active: {
                color: 'text',
                backgroundColor: 'secondaryBackgroundActive',
            },
            $$disabled: {
                color: 'secondaryText',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
        },
        tertiary: {
            fontWeight: 'bold',
            borderRadius: 'default',
            backgroundColor: 'background',
            color: 'text',
            outlineColor: 'secondaryBackground',
            borderColor: 'backgroundMedium',
            $$hover: {
                color: 'background',
                backgroundColor: 'secondaryBackgroundHover',
            },
            $$active: {
                color: 'text',
                backgroundColor: 'secondaryBackgroundActive',
            },
            $$disabled: {
                color: 'secondaryText',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
        },
        ghost: {
            fontWeight: 'bold',
            borderRadius: 'default',
            backgroundColor: 'background',
            color: 'text',
            outlineColor: 'secondaryBackground',
            borderColor: 'transparent',
            $$hover: {
                color: 'background',
                backgroundColor: 'secondaryBackgroundHover',
            },
            $$active: {
                color: 'text',
                backgroundColor: 'secondaryBackgroundActive',
            },
            $$disabled: {
                color: 'secondaryText',
                backgroundColor: 'background',
            },
        },
        slay: {
            fontWeight: 'bold',
            borderRadius: 'default',
            backgroundColor: 'errorBackground',
            color: 'background',
            outlineColor: 'errorBorder',
            borderColor: 'transparent',
            $$hover: {
                backgroundColor: 'errorBackgroundHover',
            },
            $$active: {
                color: 'text',
                backgroundColor: 'errorBackgroundActive',
            },
            $$disabled: {
                color: 'secondaryText',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
        },
    },
    tag: {
        color: 'text',
        backgroundColor: 'backgroundWeak',
        paddingTop: '$1',
        paddingBottom: '$1',
        paddingLeft: '$2',
        paddingRight: '$2',
        borderRadius: 'default',
        $$hover: {
            color: 'background',
            backgroundColor: 'secondaryBackgroundHover',
        },
        $$disabled: {
            color: 'secondaryBackgroundActive',
            backgroundColor: 'secondaryBackgroundDisabled',
        },
    },
    alerts: {
        warning: {
            backgroundColor: 'warningBackgroundWeak',
            color: 'warningIcon',
        },
        question: {
            backgroundColor: 'backgroundWeak',
            color: 'secondaryIcon',
        },
        error: {
            backgroundColor: 'errorBackgroundWeak',
            color: 'errorIcon',
        },
        success: {
            backgroundColor: 'successBackgroundWeak',
            color: 'successIcon',
        },
        info: {
            backgroundColor: 'infoBackgroundWeak',
            color: 'infoIcon',
        },
    },
    forms: {
        input: {
            borderColor: 'backgroundMedium',
            borderRadius: 'default',
            borderWidth: 1,
            paddingRight: '$10',
            paddingLeft: '$4',
            fontWeight: '500',
            fontSize: '$1',
            color: 'text',
        },
        textarea: {
            borderColor: 'backgroundMedium',
            borderRadius: 'default',
            borderWidth: 1,
            paddingLeft: '$4',
            paddingRight: '$4',
            paddingTop: '$3',
            paddingBottom: '$3',
            fontWeight: '500',
            fontSize: '$1',
            color: 'text',
            lineHeight: '$3',
        },
        radio: {
            color: 'text',
            height: '$6',
            width: '$6',
            borderRadius: 'full',
            borderWidth: 1,
            borderColor: 'secondaryBorder',
            $$hover: {
                borderColor: 'transparent',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
            $$disabled: {
                borderColor: 'secondaryBorderDisabled',
            },
        },
        checkbox: {
            borderRadius: 'subtle',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: 'text',
            color: 'text',
            $$hover: {
                borderColor: 'transparent',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
            $$checked: {
                color: 'background',
                backgroundColor: 'secondaryBackground',
                borderColor: 'secondaryBackground',
            },
            $$focus: {
                borderColor: 'transparent',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
            $$disabled: {
                borderWidth: 0,
                backgroundColor: 'secondaryBackgroundDisabled',
                color: 'secondaryBackgroundActive',
            },
        },
    },
});
export { theme as base };
//# sourceMappingURL=base.js.map